import React, { useEffect, useState } from 'react'
import { Button, Tag } from 'antd'
import { ComplianceReport, IReportData, ITaxYear, ReportDataList } from './types'
import './style.less'
import { APIFunctions } from '../../lib/apiFunctions'
import UserNote from './UserNote'
import WasInDkCheckbox from './WasInDkCheckbox'
import countryData from '../../lib/countries.json'
import { ButtonBase, Popover, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { WhereAbouts } from '../CalendarDayMenu/WhereAbouts'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        userLocationsButton: {
            width: '100%',
            height: '100%',
            '&:hover': {
                background: 'rgba(0,0,0, 0.2)',
            },
        },
        locationText: {
            overflow: 'hidden',
            margin: 'auto',
            whiteSpace: 'nowrap',
        },
        dkDayContainer: {
            marginTop: '4px',
        },
        whereAboutsContainer: {
            padding: theme.spacing(2),
        },
    })
)

function UserLocations({
    record,
    reportChange,
    apiFunctions,
    taxYear,
    reportMeta,
}: {
    record: ReportDataList
    reportChange
    apiFunctions: APIFunctions
    taxYear: ITaxYear
    reportMeta: ComplianceReport
}) {
    const [anchorUserLocation, setAnchorUserLocation] = useState<HTMLButtonElement | null>(null)
    const [whereAboutsDropdownOpen, setWhereAboutsDropdownOpen] = useState<boolean>(true)
    const userLocationsVisible = Boolean(anchorUserLocation)
    const userLocationsId = userLocationsVisible ? 'simple-popover' : undefined
    const classes = useStyles()

    function getUserLocationsContent(record: ReportDataList) {
        const locations = record.data?.user_locations
        if (!locations || locations.length < 1) return
        const distinctCountries = []
        for (var l of locations) {
            if (
                distinctCountries
                    .map((e) => e.country_code.toString().toUpperCase())
                    .includes(l.country_code.toString().toUpperCase())
            ) {
                continue
            }
            distinctCountries.push(l)
        }
        if (distinctCountries.length > 1) {
            return distinctCountries
                .map((l) => {
                    const thisCountryData = countryData[l.country_code?.toString()?.toUpperCase()]
                    return thisCountryData?.emoji ?? l.country_code
                })
                .filter((txt) => txt.length > 0)
                .join(' ')
        } else {
            const country = locations[0]
            const thisCountryData = countryData[country.country_code?.toString()?.toUpperCase()]
            return `${thisCountryData?.emoji ?? country.country_code} ${country.location_name}`
        }
    }
    if (reportMeta.is_locked || taxYear.is_locked)
        return <p className={classes.locationText}>{getUserLocationsContent(record)}</p>

    return (
        <>
            <ButtonBase
                onClick={(e) => {
                    if (reportMeta.is_locked || taxYear.is_locked) return
                    setAnchorUserLocation(e.currentTarget)
                }}
                className={classes.userLocationsButton}
            >
                <p className={classes.locationText}>{getUserLocationsContent(record)}</p>
            </ButtonBase>
            <Popover
                id={userLocationsId}
                open={userLocationsVisible}
                anchorEl={anchorUserLocation}
                onClose={() => setAnchorUserLocation(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <div className={classes.whereAboutsContainer}>
                    <WhereAbouts
                        dateClicked={record.data?.date}
                        apiFunctions={apiFunctions}
                        userId={taxYear.ll_user_id}
                        dropdownOpen={whereAboutsDropdownOpen}
                        setDropdownOpen={setWhereAboutsDropdownOpen}
                        reportChange={reportChange}
                    />
                </div>
            </Popover>
        </>
    )
}

export default function ComplianceDatesView({
    debouncedScroll,
    data,
    showBilag,
    // showUserLocations,
    reportMeta,
    apiFunctions,
    userId,
    setDataHasChanged,
    listHeight,
    reportChange,
    taxYear,
}: {
    debouncedScroll
    data: IReportData
    showBilag
    // showUserLocations
    apiFunctions: APIFunctions
    setDataHasChanged: (nv: boolean) => void
    userId: string
    reportMeta: ComplianceReport
    listHeight: number
    reportChange
    taxYear: ITaxYear
}) {
    const [rowData, setRowData] = useState<ReportDataList[]>([])
    const classes = useStyles()

    useEffect(() => {
        const rows = (data?.dates || []).map((d) => {
            return { loading: false, data: d }
        })
        setRowData(rows)
    }, [data])

    // async function onLoadMore() {
    //     setloading(true)
    //     const rows = (data?.dates || []).map((d) => {
    //         return { loading: false, data: d }
    //     })
    //     setRowData(
    //         rowData.concat(
    //             Array(10)
    //                 .fill(0)
    //                 .map((e) => {
    //                     return { loading: true, data: null }
    //                 })
    //         )
    //     )
    //     setTimeout(() => {
    //         setRowData(rows.slice(0, rowData.length + 10))
    //         setloading(false)
    //     }, 1000)
    // }

    function onScroll(e) {
        // console.log(e.target.scrollTop, e.target.scrollHeight)
        debouncedScroll(e)
    }

    function renderAppendix(record: ReportDataList) {
        if (!record.data) return
        const appendixref = record.data?.appendixref
        // appendixref? appendixref.map(r => r.id).join(', ') : null
        const len = appendixref ? appendixref.length : 0
        const includedLen = appendixref ? appendixref.filter((r) => r.include_report).length : 0
        return reportMeta.is_locked && includedLen === 0 ? null : (
            <div>
                <Button
                    onClick={() => {
                        showBilag(appendixref, record.data?.date)
                    }}
                    size="small"
                    ghost={len === 0}
                    disabled={len === 0 && reportMeta.is_locked}
                >
                    Vis {includedLen}
                    {!reportMeta.is_locked && `/${len}`}&nbsp;bilag
                </Button>
            </div>
        )
    }

    function renderUserLocations(record: ReportDataList) {
        if (!record.data) return
        return (
            <UserLocations
                record={record}
                apiFunctions={apiFunctions}
                taxYear={taxYear}
                reportChange={reportChange}
                reportMeta={reportMeta}
            />
        )
    }

    return (
        <div className="report-date-list-container">
            <div
                className="report-date-list-row report-date-list-header-row"
                style={{
                    gridTemplateColumns: `1.5fr 1fr 1.5fr 1.5fr${
                        reportMeta.include_documentation ? ' 2fr' : ''
                    }${reportMeta.include_notes ? ' 3fr' : ''}${
                        reportMeta.include_locations ? ' 2fr' : ''
                    }${reportMeta.include_roster ? ' 1fr' : ''}`,
                }}
            >
                <div className="report-table-header">Dato</div>
                <div className="report-table-header">
                    DK-dag
                    <br />
                    (x for ja)
                </div>
                <div className="report-table-header">
                    Sum af DK-dage
                    <br />
                    (6 måneder bagud)
                </div>
                <div className="report-table-header">
                    42 dages reglen
                    <br />
                    (x for overskredet)
                </div>
                {reportMeta.include_documentation && (
                    <div className="report-table-header report-table-extra-docs">
                        Bilagsreference
                    </div>
                )}
                {reportMeta.include_notes && (
                    <div className="report-table-header report-table-extra-docs">Bemærkninger</div>
                )}
                {reportMeta.include_locations && (
                    <div className="report-table-header report-table-extra-docs">Opholdssteder</div>
                )}
                {reportMeta.include_roster && (
                    <div className="report-table-header report-table-extra-docs">
                        Arbejdsdag
                        <br />
                        (x for ja)
                    </div>
                )}
            </div>
            <div
                className="report-date-list-scroller scrollbar-hidden"
                style={{ height: `${listHeight}px`, overflowY: 'scroll' }}
                onScroll={onScroll}
            >
                {rowData.map((item) => {
                    return (
                        item.data && (
                            <div
                                className="report-date-list-row"
                                style={{
                                    width: '100%',
                                    gridTemplateColumns: `1.5fr 1fr 1.5fr 1.5fr${
                                        reportMeta.include_documentation ? ' 2fr' : ''
                                    }${reportMeta.include_notes ? ' 3fr' : ''}${
                                        reportMeta.include_locations ? ' 2fr' : ''
                                    }${reportMeta.include_roster ? ' 1fr' : ''}`,
                                }}
                            >
                                <div className="report-table-cell">{item.data?.date}</div>
                                <div className="report-table-cell">
                                    <div className={classes.dkDayContainer}>
                                        <WasInDkCheckbox
                                            apiFunctions={apiFunctions}
                                            record={item.data}
                                            userId={userId}
                                            wasindk={item.data.wasindk}
                                            setDataHasChanged={setDataHasChanged}
                                            reportMeta={reportMeta}
                                        />
                                    </div>
                                </div>
                                <div className="report-table-cell">{item.data?.daycount}</div>
                                <div className="report-table-cell">
                                    {item.data?.daycount > 42 ? (
                                        <Tag color="volcano" key={item.data?.daycount}>
                                            x
                                        </Tag>
                                    ) : null}
                                </div>
                                {reportMeta.include_documentation && (
                                    <div className="report-table-cell report-table-extra-docs">
                                        {renderAppendix(item)}
                                    </div>
                                )}
                                {reportMeta.include_notes && (
                                    <div className="report-table-cell report-table-extra-docs">
                                        <UserNote
                                            note_content={item.data.note_content}
                                            apiFunctions={apiFunctions}
                                            record={item.data}
                                            userId={userId}
                                            setDataHasChanged={setDataHasChanged}
                                            reportMeta={reportMeta}
                                            fromReport
                                        />
                                    </div>
                                )}
                                {reportMeta.include_locations && (
                                    <div className="report-table-cell report-table-extra-docs">
                                        {renderUserLocations(item)}
                                    </div>
                                )}
                                {reportMeta.include_roster && (
                                    <div className="report-table-cell report-table-extra-docs">
                                        {item.data.roster_id ? 'x' : null}
                                    </div>
                                )}
                            </div>
                        )
                    )
                })}
            </div>
        </div>
    )
}
